/**
 * How a chat says which visit it is about (Noel's list item 29, 2026-10-12).
 *
 * The manager bot told a nurse "this visit MAY be cancelled" about an 11:15
 * visit. The chat banner showed only the patient's initials ("II"), and he took
 * it for his 10:00 visit. Every bot thread is also titled "Manager update", so
 * the Messages list could not tell threads apart.
 *
 * The API decides what may be shown: the full name only when the visit is hers
 * or her request is pending, the first name and last initial ("Indira I.")
 * when she held it before, initials otherwise; the street address only while
 * it is assigned to her. These helpers turn that into the same words on the
 * banner and on the list: the name as sent or "Patient I.I.", the date and
 * time, the location line, and a short status note. No React Native imports,
 * so it is testable in Node.
 */
import { formatInTz } from "@taskhealth/shared";

import type { VisitPreview } from "@/lib/api";
import type { TicketVisitContext } from "@/lib/ticket-api";
import { visitLocationLine } from "@/lib/visit-location";

/** The time fields every chat visit payload carries. */
export interface ChatVisitTimeFields {
    startTime: string | null;
    scheduledStart: string | null;
    scheduledDue: string | null;
    timezone: string;
}

/** Shown when a visit has no appointment time and no staffing window. */
export const TIME_NOT_SET_TEXT = "Time not set yet";

/**
 * "Fri, Sep 11 · 11:15 AM".
 *
 * `startTime` is a naive wall-clock time serialized with a Z (see the API's
 * serializeNaiveTimestamp), so its UTC digits ARE the local time and it is
 * formatted in UTC. A visit with no time yet shows its staffing window
 * (real instants, shown in the visit's time zone) instead, never a made-up
 * hour.
 */
export function chatVisitWhen(v: ChatVisitTimeFields): string {
    try {
        if (v.startTime) {
            return formatInTz(new Date(v.startTime), "UTC", "EEE, MMM d · h:mm a");
        }
        const tz = v.timezone || "America/New_York";
        const from = v.scheduledStart ? formatInTz(new Date(v.scheduledStart), tz, "MMM d") : null;
        const to = v.scheduledDue ? formatInTz(new Date(v.scheduledDue), tz, "MMM d") : null;
        if (from && to && from !== to) {
            return `${TIME_NOT_SET_TEXT} (${from} to ${to})`;
        }
        if (from ?? to) {
            return `${TIME_NOT_SET_TEXT} (${from ?? to})`;
        }
    } catch {
        // A malformed date falls through to the plain text.
    }
    return TIME_NOT_SET_TEXT;
}

/**
 * The Messages card's shorter form, so the patient still fits on one line:
 * "Oct 12, 11:15 AM", "Oct 14 to Oct 17" before a time is set, else
 * "Time not set".
 */
export function chatVisitWhenShort(v: ChatVisitTimeFields): string {
    try {
        if (v.startTime) {
            return formatInTz(new Date(v.startTime), "UTC", "MMM d, h:mm a");
        }
        const tz = v.timezone || "America/New_York";
        const from = v.scheduledStart ? formatInTz(new Date(v.scheduledStart), tz, "MMM d") : null;
        const to = v.scheduledDue ? formatInTz(new Date(v.scheduledDue), tz, "MMM d") : null;
        if (from && to && from !== to) {
            return `${from} to ${to}`;
        }
        if (from ?? to) {
            return (from ?? to) as string;
        }
    } catch {
        // A malformed date falls through to the plain text.
    }
    return "Time not set";
}

/** "II" -> "Patient I.I."; nothing at all -> "Patient". */
export function maskedPatientLabel(initials: string | null | undefined): string {
    const letters = (initials ?? "").replace(/[^A-Za-z]/g, "").toUpperCase();
    return letters ? `Patient ${letters.split("").join(".")}.` : "Patient";
}

/**
 * A short note when the visit is not simply hers: "Cancelled", or "Not on
 * your schedule" for a visit she had that is no longer hers. Null otherwise
 * (her visit, an offer, a pending request), and always null from an API that
 * does not send the assignment flags (only "Cancelled" then).
 */
export function chatVisitStatusNote(v: {
    status: string;
    assignedToMe?: boolean;
    wasAssignedToMe?: boolean;
}): string | null {
    if (v.status === "cancelled") {
        return "Cancelled";
    }
    if (v.assignedToMe === false && v.wasAssignedToMe === true) {
        return "Not on your schedule";
    }
    return null;
}

/** Everything the chat banner prints about its visit. */
export interface ChatVisitSummary {
    /** "Ines Ibarra", "Ines I." (a visit she held before), or "Patient I.I.". */
    name: string;
    nameIsInitials: boolean;
    when: string;
    /** The location line, or null when the API sent none. */
    location: string | null;
    statusNote: string | null;
}

/** The banner's text for a visit preview. */
export function summarizeVisitPreview(p: VisitPreview): ChatVisitSummary {
    const fullName = `${p.patientFirstName ?? ""} ${p.patientLastName ?? ""}`.trim();
    return {
        name: fullName || maskedPatientLabel(p.patientInitials),
        nameIsInitials: fullName === "",
        when: chatVisitWhen(p),
        location: p.location ? visitLocationLine(p.location) : null,
        statusNote: chatVisitStatusNote(p)
    };
}

/**
 * The one line a Messages card prints under its title:
 * "Sep 11, 11:15 AM · Ines Ibarra", plus " · Cancelled" when the visit
 * was cancelled.
 */
export function ticketVisitLine(v: TicketVisitContext): string {
    const name = v.patientNameIsInitials ? maskedPatientLabel(v.patientName) : v.patientName;
    const parts = [chatVisitWhenShort(v), name];
    if (v.status === "cancelled") {
        parts.push("Cancelled");
    }
    return parts.filter((x) => x.trim() !== "").join(" · ");
}
